import { useQuery } from '@tanstack/react-query';
import { fabric } from 'fabric';
import { FabricJSCanvas, useFabricJSEditor } from 'fabricjs-react';
import _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useUpdateStatisticsMutation } from 'api/adminApi';
import { getDashboard, getMapDashboard, getPlantImage } from 'api/dashboardApi';
import {
  getPartList,
  getSectionListByFactory,
  getZoneListByFactory,
} from 'api/deviceApi';
import { DeviceSearchRequest } from 'api/DeviceRequest';
import {
  dashboardKeys,
  mapDashboardKeys,
  partKeys,
  plantImageKeys,
  sectionKeys,
  zoneKeys,
} from 'api/quires/queryKeys';
import ZoneInfoModal from 'components/device/position/ZoneInfoModal';
import DeviceSearchBox from 'components/searchbox/DeviceSearchBox';
import Factory from 'model/Factory';
import SectionBoard from 'model/SectionBoard';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { DEVICE_MODAL_TYPE, STATISTICS_TYPE } from 'types/enumType';
import { clearCanvas } from 'util/canvasUtil';
import {
  createRectByEquipmentAndRatio,
  getScaleAndRatio,
  isSuperAdmin,
} from 'util/commonUtil';

import { decryptId, decryptString } from '../../util/encryptUtil';

export default function StatusBoard() {
  const { t } = useTranslation();
  const ZONE_SQUARE_IMAGE = `${process.env.PUBLIC_URL}/assets/images/content/zone-square.png`;

  const userInfo = useRecoilValue(userInfoState);
  const role = decryptString(userInfo.role);
  const factoryId = decryptId(userInfo.factoryId ?? '');
  const isSuper = isSuperAdmin(role, factoryId);
  const [searchRequest, setSearchRequest] = useState<DeviceSearchRequest>({
    localId: userInfo.local,
    factoryId,
  });
  const [plantInfo, setPlantInfo] = useState<Factory | null>(null);
  const [planImage, setPlanImage] = useState<string>('');
  const [isCanvasReady, setIsCanvasReady] = useState<boolean>(false);
  const [imgCanvas, setImgCanvas] = useState<fabric.Image>();
  const { editor, onReady } = useFabricJSEditor();

  const [activeSection, setActiveSection] = useState<SectionBoard | null>(null);
  const [posX, setPosX] = useState<number>(0);
  const [posY, setPosY] = useState<number>(0);

  const [isZoneInfoModalOpen, setIsZoneInfoModalOpen] = useState(false);

  const resizeCanvas = (ww: number, wh: number) => {
    // TODO Uncaught TypeError: Cannot destructure property 'width' of 'imgCanvas' as it is undefined.
    if (!imgCanvas || !editor?.canvas || !editor.canvas.backgroundImage) return;

    const { scale: newScale } = getScaleAndRatio(imgCanvas, {
      width: ww,
      height: wh,
    });
    const iw = imgCanvas.width!;
    const ih = imgCanvas.height!;

    const afterWidth = newScale * iw;
    const afterHeight = newScale * ih;

    // 배경 이미지 스케일
    imgCanvas.scale(newScale);
    editor?.canvas.setBackgroundImage(
      imgCanvas,
      editor?.canvas.renderAll.bind(editor?.canvas),
      { scaleX: newScale, scaleY: newScale },
    );

    editor?.canvas.setDimensions({
      width: afterWidth,
      height: afterHeight,
    });

    // 기존 오브젝트 제거
    const background = editor.canvas.backgroundImage!;
    editor.canvas.clear();
    editor.canvas.setBackgroundImage(
      background,
      editor?.canvas.renderAll.bind(editor?.canvas),
    );

    if (partList && partList?.list?.length > 0) {
      editor.canvas.add(
        ...partList.list.map((partItem) =>
          createRectByEquipmentAndRatio(
            partItem,
            DEVICE_MODAL_TYPE.PART,
            newScale,
            false,
          ),
        ),
      );
    }
    if (zoneList && zoneList?.list?.length > 0) {
      editor.canvas.add(
        ...zoneList.list.map((zoneItem) =>
          createRectByEquipmentAndRatio(
            zoneItem,
            DEVICE_MODAL_TYPE.AREA,
            newScale,
            false,
          ),
        ),
      );
    }

    if (sectionList && sectionList?.list?.length > 0) {
      const activeSectionIdList =
        activeZoneList?.list?.map((item) => item.sectionId) ?? [];
      sectionList.list.forEach((zoneItem) => {
        const isActive = activeSectionIdList?.includes(zoneItem.id);
        const rect = createRectByEquipmentAndRatio(
          zoneItem,
          DEVICE_MODAL_TYPE.SECTION,
          newScale,
          false,
        );
        if (isActive) {
          rect.name = `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`;
          rect.fill = 'rgba(75, 181, 5, 0.4)';
        }
        editor.canvas.add(rect);
        // if (!isActive) {
        //   canvas.add(rect);
        // } else {
        //   fabric.Image.fromURL(ZONE_SQUARE_IMAGE, (oImg) => {
        //     const { x, y } = rect.getCenterPoint();
        //     oImg.scale(scale);
        //     oImg.setOptions({
        //       left: x,
        //       top: y,
        //       width: 50,
        //       height: 50,
        //       originX: 'center',
        //       originY: 'center',
        //     });
        //     const group = new fabric.Group([rect, oImg], {
        //       selectable: false,
        //       name: `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`,
        //     });
        //     canvas.add(group);
        //   });
        // }
      });
    }
  };

  const handleCanvasReady = (canvas: fabric.Canvas) => {
    fabric.Image.fromURL(planImage, (img) => {
      setImgCanvas(img);

      const { scale } = getScaleAndRatio(img, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      img.scale(scale);
      canvas.setDimensions({
        width: scale * img.width!,
        height: scale * img.height!,
      });
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));

      if (partList && partList?.list?.length > 0) {
        canvas.add(
          ...partList.list.map((partItem) =>
            createRectByEquipmentAndRatio(
              partItem,
              DEVICE_MODAL_TYPE.PART,
              scale,
              false,
            ),
          ),
        );
      }
      if (zoneList && zoneList?.list?.length > 0) {
        canvas.add(
          ...zoneList.list.map((zoneItem) =>
            createRectByEquipmentAndRatio(
              zoneItem,
              DEVICE_MODAL_TYPE.AREA,
              scale,
              false,
            ),
          ),
        );
      }

      if (sectionList && sectionList?.list?.length > 0) {
        const activeSectionIdList =
          activeZoneList?.list?.map((item) => item.sectionId) ?? [];
        sectionList.list.forEach((zoneItem) => {
          const isActive = activeSectionIdList?.includes(zoneItem.id);
          const rect = createRectByEquipmentAndRatio(
            zoneItem,
            DEVICE_MODAL_TYPE.SECTION,
            scale,
            false,
          );
          if (isActive) {
            rect.name = `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`;
            rect.fill = 'rgba(75, 181, 5, 0.4)';
          }
          canvas.add(rect);
          // if (!isActive) {
          //   canvas.add(rect);
          // } else {
          //   fabric.Image.fromURL(ZONE_SQUARE_IMAGE, (oImg) => {
          //     const { x, y } = rect.getCenterPoint();
          //     oImg.scale(scale);
          //     oImg.setOptions({
          //       left: x,
          //       top: y,
          //       width: 50,
          //       height: 50,
          //       originX: 'center',
          //       originY: 'center',
          //     });
          //     const group = new fabric.Group([rect, oImg], {
          //       selectable: false,
          //       name: `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`,
          //     });
          //     canvas.add(group);
          //   });
          // }
        });
      }
      onReady(canvas);
    });

    // canvas.selection = false;
  };

  // 이미지 가져오기
  const { data: plantImageInfo } = useQuery(
    plantImageKeys.all,
    () => getPlantImage(searchRequest.factoryId),
    { enabled: !isSuper },
  );

  // 영역 그리기
  const {
    data: partList,
    refetch: refetchPart,
    remove: removePart,
  } = useQuery(
    partKeys.list(searchRequest.factoryId),
    () => getPartList(searchRequest),
    { enabled: !isSuper },
  );
  const {
    data: zoneList,
    refetch: refetchZone,
    remove: removeZone,
  } = useQuery(
    zoneKeys.listByFactory(searchRequest.factoryId!),
    () => getZoneListByFactory(searchRequest),
    { enabled: !isSuper },
  );
  const {
    data: sectionList,
    refetch: refetchSection,
    remove: removeSection,
  } = useQuery(
    sectionKeys.listByFactory(searchRequest.factoryId!),
    () => getSectionListByFactory(searchRequest),
    { enabled: false },
  );

  // 현재 작업중인 섹션 표기
  const {
    data: activeZoneList,
    refetch: refetchActiveZone,
    remove: removeActiveZone,
  } = useQuery(
    mapDashboardKeys.list(searchRequest.factoryId),
    () => getMapDashboard(searchRequest.factoryId),
    {
      enabled: !isSuper,
    },
  );

  const { data } = useQuery({
    queryKey: dashboardKeys.detail(searchRequest.factoryId),
    queryFn: () => getDashboard(searchRequest.factoryId),
    enabled: Number(searchRequest.factoryId) > 0,
  });

  useEffect(() => {
    if (!userInfo || userInfo.local === searchRequest.localId) {
      return;
    }
    setSearchRequest((prev) => ({
      ...prev,
      localId: userInfo.local,
      factoryId: '',
    }));
  }, [userInfo.local]);

  useEffect(() => {
    if (activeZoneList) {
      removeSection();
      refetchSection();
    }
  }, [activeZoneList]);

  useEffect(() => {
    if (!sectionList) {
      return;
    }

    const tmpImage = isSuper
      ? (plantInfo?.image ?? '')
      : (plantImageInfo?.plantImage ?? '');
    setPlanImage(tmpImage);
  }, [sectionList]);

  const debounceResize = _.debounce(resizeCanvas, 200);

  useEffect(() => {
    const handleWindowResize = () => {
      if (!editor?.canvas && isCanvasReady) {
        return;
      }

      const { innerWidth: width, innerHeight: height } = window;

      debounceResize(width, height);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [editor]);

  const handleCloseZoneInfoModal = () => {
    setIsZoneInfoModalOpen(false);
  };

  const handleSearchSubmit = (
    factoryInfo: Factory | null,
    event?: FormEvent,
  ) => {
    if (event) {
      event.preventDefault();
    }

    setSearchRequest((prev) => ({
      ...prev,
      factoryId: (factoryInfo?.id ?? '').toString(),
    }));

    clearCanvas(editor?.canvas);

    setPlanImage('');
    setPlantInfo(factoryInfo);
    setIsCanvasReady(false);

    setTimeout(() => {
      removePart();
      removeZone();
      removeActiveZone();

      refetchPart();
      refetchZone();
      refetchActiveZone();
    }, 100);
  };

  const handleClickActiveZone = (e: fabric.IEvent<MouseEvent>) => {
    const selectObj = e.target;
    if (!selectObj) {
      return false;
    }

    const [type, typeId, isActive] = selectObj.name!.split('/');

    if (type === DEVICE_MODAL_TYPE.SECTION && isActive === 'active') {
      const { clientX, clientY } = e.e;
      setPosX(clientX);
      setPosY(clientY);

      setActiveSection(
        activeZoneList!.list.find((item) => item.sectionId === +typeId)!,
      );
      // modal open
      const handleOpenZoneInfoModal = () => {
        setIsZoneInfoModalOpen(true);
      };
      handleOpenZoneInfoModal();

      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!editor?.canvas && isCanvasReady) {
      return;
    }

    const enablePanning = () => {
      let isDragging = false;
      let lastX: number = 0;
      let lastY: number = 0;

      editor?.canvas.on('mouse:down', (event) => {
        const pointer = editor?.canvas.getPointer(event.e);
        lastX = pointer.x;
        lastY = pointer.y;
        isDragging = true;
      });

      editor?.canvas.on('mouse:move', (event) => {
        if (!isDragging) return;

        const pointer = editor.canvas.getPointer(event.e);
        lastX = pointer.x;
        lastY = pointer.y;

        const delta = new fabric.Point(event.e.movementX, event.e.movementY);
        editor.canvas.relativePan(delta);
      });

      editor?.canvas.on('mouse:up', (event) => {
        isDragging = false;

        const pointer = editor?.canvas.getPointer(event.e);
        const diffX = Math.abs(pointer.x - lastX);
        const diffY = Math.abs(pointer.y - lastY);

        if (diffX === 0 && diffY === 0) {
          handleClickActiveZone(event);
        }
      });

      editor?.canvas.on('mouse:wheel', (opt) => {
        const delta = opt.e.deltaY;
        let zoom = editor.canvas.getZoom();
        zoom *= 0.999 ** delta;
        if (zoom > 20) zoom = 20;
        if (zoom <= 1) zoom = 1;
        editor.canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();

        const vpt = editor.canvas.viewportTransform;
        const canvasW = editor.canvas.getWidth();
        const canvasH = editor.canvas.getHeight();
        if (vpt && zoom === 1) {
          if (zoom < 400 / canvasW) {
            vpt[4] = 200 - (canvasW * zoom) / 2;
            vpt[5] = 200 - (canvasW * zoom) / 2;
          } else {
            if (vpt[4] >= 0) {
              vpt[4] = 0;
            } else if (vpt[4] < editor.canvas.getWidth() - canvasW * zoom) {
              vpt[4] = editor.canvas.getWidth() - canvasW * zoom;
            }
            if (vpt[5] >= 0) {
              vpt[5] = 0;
            } else if (vpt[5] < editor.canvas.getHeight() - canvasH * zoom) {
              vpt[5] = editor.canvas.getHeight() - canvasH * zoom;
            }
          }
        }
      });
    };

    enablePanning();
  }, [editor?.canvas]);

  const { pathname } = useLocation();
  const { mutate } = useUpdateStatisticsMutation();

  useEffect(() => {
    mutate(STATISTICS_TYPE.DASHBOARD, {});
  }, [pathname]);

  return (
    <div className={'board-wrap'}>
      <div className={'dash-wrap'}>
        {isSuper && (
          <DeviceSearchBox
            searchRequest={searchRequest}
            isLoading={false}
            onSubmit={handleSearchSubmit}
          />
        )}
        {data?.info && (
          <>
            <section className={'main-table'}>
              <table className={'tbl_hl01'}>
                <colgroup>
                  <col width={'32%'} />
                  <col width={'40%'} />
                  <col width={'28%'} />
                </colgroup>
                <tbody>
                  <tr>
                    <th className={'under'} rowSpan={3}>
                      <Link to={'/loto-status/conclusion'}>
                        {t('home.order')}
                        <br />
                        <b>{`(${
                          data.info.orderLockAttachCnt +
                          data.info.orderLockConfirmCnt +
                          data.info.orderUnlockApproveCnt
                        })`}</b>
                      </Link>
                    </th>

                    <td>{t('home.locked')}</td>
                    <td>{data.info.orderLockAttachCnt}</td>
                  </tr>
                  <tr>
                    <td className={'nowrap'}>{t('home.confirmLocking')}</td>
                    <td>{data.info.orderLockConfirmCnt}</td>
                  </tr>
                  <tr>
                    <td className={'nowrap'}>{t('home.lotoOpen')}</td>
                    <td>{data.info.orderUnlockApproveCnt}</td>
                  </tr>
                  <tr>
                    <th className={'under'} rowSpan={3}>
                      <Link to={'/loto-status/action'}>
                        {t('home.lock')}
                        <br />
                        <b>{`(${data.info.totalSettingCnt})`}</b>
                      </Link>
                    </th>
                    <td>{t('home.notLocking')}</td>
                    <td>{data.info.totalNothingCnt}</td>
                  </tr>
                  <tr>
                    <td>{t('home.locking')}</td>
                    <td>{data.info.totalLockedCnt}</td>
                  </tr>
                  <tr>
                    <td>{t('home.unlocking')}</td>
                    <td>{data.info.totalUnlockCnt}</td>
                  </tr>
                </tbody>
              </table>
            </section>
            <section className={'main-progressbar'}>
              <table className={'tbl_hl01 tbl_ht01'}>
                <colgroup>
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>{t('home.sortation')}</td>
                    <th>{t('home.prod')}</th>
                    <th>{t('home.elec')}</th>
                    <th>{t('home.mech')}</th>
                    <th>{t('home.etc')}</th>
                  </tr>
                  <tr>
                    <th>{t('home.notLocking')}</th>
                    <td>{data.info.prodNothingCnt}</td>
                    <td>{data.info.elecNothingCnt}</td>
                    <td>{data.info.mechNothingCnt}</td>
                    <td>{data.info.etcNothingCnt}</td>
                  </tr>
                  <tr>
                    <th>{t('home.locking')}</th>
                    <td>{data.info.prodLockedCnt}</td>
                    <td>{data.info.elecLockedCnt}</td>
                    <td>{data.info.mechLockedCnt}</td>
                    <td>{data.info.etcLockedCnt}</td>
                  </tr>
                  <tr>
                    <th>{t('home.unlocking')}</th>
                    <td>{data.info.prodUnlockCnt}</td>
                    <td>{data.info.elecUnlockCnt}</td>
                    <td>{data.info.mechUnlockCnt}</td>
                    <td>{data.info.etcUnlockCnt}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
      <div className={'canvas-container'}>
        {planImage && (
          <FabricJSCanvas
            className={'sample-canvas'}
            onReady={handleCanvasReady}
          />
        )}
      </div>
      <ZoneInfoModal
        info={activeSection}
        isOpen={isZoneInfoModalOpen}
        onClose={handleCloseZoneInfoModal}
        posX={posX}
        posY={posY}
      />
    </div>
  );
}
